/**
 * Configures the generated @hey-api fetch client.
 *
 * All API calls target `/api/*`. In development, Vite proxies that prefix
 * to the FastAPI backend (see `vite.config.ts`); in production, nginx does
 * the same rewrite (see `nginx.conf`). The browser never knows the backend
 * URL — only the same-origin `/api` path.
 *
 * Importing this module has the side effect of configuring the singleton
 * client. `src/lib/api/index.ts` does this on first import.
 */

import { client } from "@/lib/api/generated/client.gen";
import type {
  DownloadEngagementFileData,
  DownloadPortalFileData,
} from "@/lib/api/generated/types.gen";
import { logger } from "@/lib/logger";

/**
 * Structured error thrown by all SDK calls.
 *
 * @hey-api/client-fetch with `throwOnError: true` throws just the parsed
 * response body by default, which loses the HTTP status. The error
 * interceptor below wraps every non-OK response in this shape so callers
 * can branch on `status` (e.g. 401 vs 403 vs 400) and read FastAPI's
 * `{ detail: ... }` payload from `body`.
 */
export interface ApiError {
  status: number;
  body: unknown;
  /** Seconds from the response's `Retry-After` header (a 429), when it sent one. */
  retryAfter?: number;
}

/** HTTP status of an error thrown by an SDK call (undefined for anything else). */
export function apiErrorStatus(error: unknown): number | undefined {
  return (error as Partial<ApiError> | undefined)?.status;
}

/** FastAPI's `detail` string from an SDK error, when there is one. */
export function apiErrorDetail(error: unknown): string | undefined {
  const detail = (error as { body?: { detail?: unknown } } | undefined)?.body?.detail;
  return typeof detail === "string" ? detail : undefined;
}

/** The `kind` and `ids` of a domain refusal (a 409 `DomainErrorResponse`), when it names any. */
export function apiErrorRefusal(error: unknown): { kind: string | null; ids: string[] } {
  const body = (error as { body?: { kind?: unknown; ids?: unknown } } | undefined)?.body;
  return {
    kind: typeof body?.kind === "string" ? body.kind : null,
    ids: Array.isArray(body?.ids)
      ? body.ids.filter((id): id is string => typeof id === "string")
      : [],
  };
}

/** Seconds a throttled SDK call asks to wait (`Retry-After`), when the answer said. */
export function apiErrorRetryAfter(error: unknown): number | undefined {
  const seconds = (error as Partial<ApiError> | undefined)?.retryAfter;
  return typeof seconds === "number" ? seconds : undefined;
}

const API_BASE_URL = "/api";

/**
 * Same-origin path that starts BOI Google SSO (`GET /auth/oauth/google`).
 *
 * The browser must NAVIGATE here (a full page load), not fetch it: the backend
 * answers with a redirect to Supabase/Google and the round trip ends on the
 * backend's callback, which sets the session cookie and lands on `/cockpit`.
 */
export const GOOGLE_SSO_START_PATH = `${API_BASE_URL}/auth/oauth/google`;

client.setConfig({
  baseUrl: API_BASE_URL,
  throwOnError: true,
});

/**
 * Same-origin URL of a shared file's download (`GET /portal/files/{file_id}/download`).
 *
 * A plain link, not a fetch: the session cookie authenticates it and the backend answers
 * with an attachment, so the browser saves the file without holding it in memory first.
 */
export function portalFileDownloadUrl(fileId: string): string {
  return client.buildUrl<DownloadPortalFileData>({
    url: "/portal/files/{file_id}/download",
    path: { file_id: fileId },
  });
}

/** Same-origin URL of an engagement file's download, for staff; a plain link, as above. */
export function engagementFileDownloadUrl(engagementId: string, fileId: string): string {
  return client.buildUrl<DownloadEngagementFileData>({
    url: "/engagements/{engagement_id}/files/{file_id}/download",
    path: { engagement_id: engagementId, file_id: fileId },
  });
}

client.interceptors.request.use((request) => {
  logger.debug("[api]", request.method, request.url);
  return request;
});

client.interceptors.response.use((response) => {
  logger.debug("[api]", response.status, response.url);
  return response;
});

client.interceptors.error.use((error, response) => {
  // Transport-level failures (offline, DNS, backend down) reach this
  // interceptor without an HTTP response. Guard it — dereferencing
  // response.status would throw a TypeError that masks the real error.
  // status 0 marks "no response": the query client retries it (unlike
  // 401/403), and error UIs fall back to the detail message.
  if (!response) {
    return {
      status: 0,
      body: { detail: error instanceof Error ? error.message : "Network error" },
    } satisfies ApiError;
  }
  return {
    status: response.status,
    body: error,
    retryAfter: retryAfterSeconds(response.headers.get("Retry-After")),
  } satisfies ApiError;
});

/** The backend sends `Retry-After` as whole seconds; the HTTP-date form is not used. */
function retryAfterSeconds(header: string | null): number | undefined {
  return header !== null && /^\d+$/.test(header) ? Number(header) : undefined;
}
